/**
 * ZonePeek — modal that lets the viewer inspect the contents of one of
 * their zones (deck face-down, abyss face-up, banishment face-up).
 *
 * Per Wonders rules:
 *   - Deck contents are PRIVATE — even the owner can only count, not
 *     reorder. We render the count + face-down stack; clicking a card
 *     does nothing.
 *   - Abyss is PUBLIC — anyone can see contents. Cards render face-up
 *     and are draggable: the viewer can pull a card out of the abyss
 *     into hand or back to a realm.
 *   - Banishment is PUBLIC — same as abyss.
 *
 * The modal auto-dismisses the moment a drag starts (the dnd-kit drag
 * overlay sits at z-50 while the modal is z-9999; without auto-close
 * the dragged card would render behind the modal backdrop and the
 * user couldn't see what they were dragging).
 */

import { useDndContext, useDraggable } from '@dnd-kit/core';
import { type ReactElement, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { CardData, PlayerId, ZoneRef } from '../types.js';

type Visibility = 'private' | 'public';

type Props = {
  readonly title: string;
  readonly cardIds: readonly string[];
  readonly visibility: Visibility;
  readonly cards: ReadonlyMap<string, CardData>;
  readonly imageUrl: (cardId: string) => string;
  readonly onClose: () => void;
  /** When set with a public zone, peek cards become drag sources whose
   *  drop event resolves to MoveCard from this zone. */
  readonly source?: { zone: 'abyss' | 'banished' | 'tokens'; owner: PlayerId };
};

export function ZonePeek({
  title,
  cardIds,
  visibility,
  cards,
  imageUrl,
  onClose,
  source,
}: Props): ReactElement {
  // Escape-to-close — keyboard accessibility for the peek modal.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // While a drag is in flight we keep the modal MOUNTED (otherwise
  // unmounting kills the PeekCard's useDraggable registration mid-drag
  // and the drop never lands), but make the backdrop transparent and
  // click-through so the user can see and target underlying drop
  // zones (hand, realm tiles).
  const dnd = useDndContext();
  const dragActive = dnd.active !== null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={`fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm ${
        dragActive ? 'pointer-events-none bg-transparent' : 'bg-cosmos-950/90'
      }`}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        className={`max-h-[80vh] w-[90vw] max-w-[900px] overflow-y-auto rounded-md border border-cosmos-700 bg-cosmos-900 p-4 shadow-2xl transition-opacity ${
          dragActive ? 'pointer-events-none opacity-20' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-center justify-between">
          <h2 className="font-bold text-amber-200 text-lg">
            {title} <span className="text-cosmos-400 text-sm">({cardIds.length})</span>
          </h2>
          <button
            type="button"
            className="rounded bg-cosmos-800 px-2 py-1 text-cosmos-200 text-sm hover:bg-cosmos-700"
            onClick={onClose}
          >
            ✕ Close
          </button>
        </header>

        {cardIds.length === 0 ? (
          <p className="py-8 text-center text-cosmos-500">Empty.</p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-2">
            {cardIds.map((id, idx) => {
              const card = cards.get(id);
              const key = `${id}-${idx}`;
              if (visibility === 'private') {
                // Face-down rendering — the owner sees a stack of card-backs;
                // viewers don't get to read deck contents (matches rules).
                return (
                  <div
                    key={key}
                    role="img"
                    aria-label="Face-down card"
                    className="aspect-[2/3] rounded-sm bg-gradient-to-br from-cosmos-700 to-cosmos-900 ring-1 ring-cosmos-600"
                  />
                );
              }
              if (source) {
                return (
                  <PeekCard
                    key={key}
                    cardId={id}
                    name={card?.name ?? id}
                    imageUrl={imageUrl(id)}
                    source={source}
                    index={idx}
                  />
                );
              }
              // Visibility public but no source given (e.g. spectator view).
              // Render non-draggable thumbnail.
              return (
                <div
                  key={key}
                  className="relative aspect-[2/3] overflow-hidden rounded-sm bg-cosmos-800 ring-1 ring-cosmos-600"
                  title={card?.name ?? id}
                >
                  <img
                    src={imageUrl(id)}
                    alt={card?.name ?? id}
                    className="h-full w-full object-cover"
                    draggable={false}
                    onError={(e) => {
                      const el = e.currentTarget;
                      el.style.display = 'none';
                    }}
                  />
                  <span className="absolute inset-x-0 bottom-0 truncate bg-cosmos-950/80 px-1 py-0.5 text-[10px] text-cosmos-100">
                    {card?.name ?? id}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Single peek-modal thumbnail backed by useDraggable. The dnd-kit
 * DragEndEvent handler in Table.tsx reads `dragData.source` to dispatch
 * a MoveCard from the abyss/banishment to the dropped zone. The
 * thumbnail's translate transform is applied inline so the drag handle
 * follows the cursor; the parent modal auto-closes on drag-start so the
 * user actually sees the move.
 */
function PeekCard({
  cardId,
  name,
  imageUrl,
  source,
  index,
}: {
  cardId: string;
  name: string;
  imageUrl: string;
  source: { zone: 'abyss' | 'banished' | 'tokens'; owner: PlayerId };
  index: number;
}) {
  const dragData: { cardId: string; source: ZoneRef } = {
    cardId,
    source: { kind: source.zone, owner: source.owner },
  };
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `peek-${source.zone}-${cardId}-${index}`,
    data: dragData,
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`relative aspect-[2/3] cursor-grab overflow-hidden rounded-sm bg-cosmos-800 ring-1 ring-cosmos-600 active:cursor-grabbing ${isDragging ? 'opacity-60' : ''}`}
      title={`${name} — drag to move out of the pile`}
    >
      <img
        src={imageUrl}
        alt={name}
        className="h-full w-full object-cover"
        draggable={false}
        onError={(e) => {
          const el = e.currentTarget;
          el.style.display = 'none';
        }}
      />
      <span className="absolute inset-x-0 bottom-0 truncate bg-cosmos-950/80 px-1 py-0.5 text-[10px] text-cosmos-100">
        {name}
      </span>
    </div>
  );
}
